import { useState } from "react";
import { cx } from "../cx";
import { Icon } from "../Icon";
import { Popover, useDisclosure } from "../Popover";
import styles from "./DatePicker.module.css";
import { addMonths, formatDateDefault, startOfMonth, type WeekStart } from "./dates";
import { MonthCalendar } from "./MonthCalendar";

export interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  weekStart?: WeekStart;
  format?: (d: Date) => string;
  placeholder?: string;
  disabled?: boolean;
  /** Injected for tests; defaults to now. */
  today?: Date;
  className?: string;
  "aria-label"?: string;
}

export function DatePicker({
  value,
  onChange,
  weekStart = "MONDAY",
  format = formatDateDefault,
  placeholder = "Select date",
  disabled,
  today = new Date(),
  className,
  ...rest
}: DatePickerProps) {
  const { open, toggle, close, anchorRef } = useDisclosure();
  const [month, setMonth] = useState(() => startOfMonth(value ?? today));
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={cx(styles.trigger, className)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={rest["aria-label"]}
        disabled={disabled}
        onClick={() => {
          if (!open) setMonth(startOfMonth(value ?? today));
          toggle();
        }}
      >
        <span className={cx(!value && styles.placeholder)}>{value ? format(value) : placeholder}</span>
        <Icon name="calendar" size={16} className={styles.triggerIcon} />
      </button>
      <Popover open={open} onClose={close} anchorRef={anchorRef} surface="card" className={styles.panel} role="dialog">
        <MonthCalendar
          month={month}
          weekStart={weekStart}
          today={today}
          selected={value ? { start: value, end: value } : null}
          onPrev={() => setMonth((m) => addMonths(m, -1))}
          onNext={() => setMonth((m) => addMonths(m, 1))}
          onPick={(d) => {
            onChange(d);
            close();
          }}
        />
      </Popover>
    </>
  );
}
